import { useCallback, useContext, useMemo, useState } from 'react'
import { View, Text, TextInput, Pressable, Platform } from 'react-native'
import axios from 'axios'
import { LanguageContext } from '../../../i18n'
import { useTheme } from '../../../theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { API_URL, API_ENDPOINTS } from '../../../../constants/api'
import { getB2bContactFormStyles } from './B2bContactForm.styles'
import { validateContactLeadPayload } from '../../helpers/orgFormValidation.helper'
import { sanitizeEmailInput, sanitizeInput } from '../../../helpers/sanitizeInput.helper'
import { useCaptchaToken } from '../../../auth/turnstile/useCaptchaToken'

export function B2bContactForm() {
  const theme = useTheme()
  const styles = useMemo(() => getB2bContactFormStyles(theme), [theme])
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('B2bContactForm must be used within LanguageProvider')
  }
  const { t } = languageContext
  const c = t.b2bContact
  const { getCaptchaToken, resetCaptcha, CaptchaWidget } = useCaptchaToken()

  const [orgName, setOrgName] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const canSubmit = orgName.trim().length >= 2 && contactName.trim().length >= 2 && contactEmail.trim().length >= 3

  const handleSubmit = useCallback(async () => {
    setError(null)
    const validation = validateContactLeadPayload({
      orgName,
      contactName,
      contactEmail,
      message,
    })
    if (!validation.ok || !validation.value) {
      setError(validation.error ?? c.errorFallbackValidation)
      return
    }
    setBusy(true)
    try {
      const captchaToken = await getCaptchaToken()
      await axios.post(`${API_URL}${API_ENDPOINTS.accounts.contactLead}`, {
        orgName: validation.value.orgName,
        contactName: validation.value.contactName,
        contactEmail: validation.value.contactEmail,
        message: validation.value.message || undefined,
        turnstileToken: captchaToken,
      })
      resetCaptcha()
      setSuccess(true)
    } catch (e: unknown) {
      resetCaptcha()
      const msg = axios.isAxiosError(e) && e.response?.data?.error
        ? String(e.response.data.error)
        : c.errorFallbackSubmit
      setError(msg)
    } finally {
      setBusy(false)
    }
  }, [orgName, contactName, contactEmail, message, getCaptchaToken, resetCaptcha, c])

  if (success) {
    return (
      <View style={styles.container}>
        <Text style={styles.successText}>{c.successMessage}</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{c.title}</Text>
      <Text style={styles.subtitle}>{c.subtitle}</Text>

      <Text style={styles.label}>{c.orgNameLabel}</Text>
      <TextInput
        value={orgName}
        onChangeText={(text) => setOrgName(sanitizeInput(text, true))}
        style={styles.input}
        placeholder={c.orgNamePlaceholder}
        editable={!busy}
      />

      <Text style={styles.label}>{c.contactNameLabel}</Text>
      <TextInput
        value={contactName}
        onChangeText={(text) => setContactName(sanitizeInput(text, true))}
        style={styles.input}
        placeholder={c.contactNamePlaceholder}
        editable={!busy}
      />

      <Text style={styles.label}>{c.emailLabel}</Text>
      <TextInput
        value={contactEmail}
        onChangeText={(text) => setContactEmail(sanitizeEmailInput(text))}
        style={styles.input}
        placeholder={c.emailPlaceholder}
        keyboardType="email-address"
        autoCapitalize="none"
        editable={!busy}
      />

      <Text style={styles.label}>{c.messageLabel}</Text>
      <TextInput
        value={message}
        onChangeText={(text) => setMessage(sanitizeInput(text, true))}
        style={[styles.input, styles.textArea]}
        placeholder={c.messagePlaceholder}
        multiline
        editable={!busy}
      />

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      <Pressable
        accessibilityRole="button"
        disabled={!canSubmit || busy}
        onPress={() => { void handleSubmit() }}
        style={({ pressed, hovered }) => [
          styles.submitBtn,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.pressed,
          hovered && Platform.OS === 'web' && styles.pressed,
          (!canSubmit || busy) && styles.submitBtnDisabled,
        ]}
      >
        <Text style={styles.submitBtnText}>{c.submitButton}</Text>
      </Pressable>

      {CaptchaWidget}

      <Text style={styles.memo}>{c.scheduleMemo}</Text>
    </View>
  )
}
